import React, {useCallback, useContext} from 'react';
import {useNavigate} from 'react-router-dom';


import {ConfirmationContext} from 'src/contexts/confirmation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';

import {CreateUserStep} from '../../components/create-user-step/create-user-step';
import {CREATE_USER_STEPS} from '../../constants';

import {UserLimitsController} from './user-limits.controller';

import type {ConfirmationContextType} from 'src/contexts/confirmation-context';

type Props = {
    userId: number,
    onNext?: () => void,
    onBack: () => void,
    canSetupAdminRole: boolean,
    canViewLimits: boolean,
    onRedirectToStep: (step: CREATE_USER_STEPS) => void,
};

export const CreateUserLimitsStep = (props: Props) => {
    const {
        userId,
        onNext,
        onBack,
        canSetupAdminRole,
        canViewLimits,
        onRedirectToStep,
    } = props;
    const {hasAccess} = useAdminAccessRight();
    const navigate = useNavigate();
    const {userCreateSubmit, userFieldChange} = useEventTracking();

    const onClose = useCallback(() => {
        userCreateSubmit({place: 'User Limits', action: 'Close'});
        navigate(`/profile/user/${userId}`);
    }, [userId, navigate, userCreateSubmit]);

    const onDone = useCallback(() => {
        userCreateSubmit({place: 'User Limits', action: 'Done'});
        navigate(`/profile/user/${userId}`);
    }, [navigate, userCreateSubmit, userId]);

    const {onShowModal} = useContext<ConfirmationContextType>(ConfirmationContext);

    const onFieldChange = useCallback((parameterName: string) => {
        userFieldChange({
            section: 'User Limits',
            viewType: 'New',
            parameterName,
        });
    }, [userFieldChange]);

    return (
        <ValidationContextProvider>
            <UserLimitsController
                onFieldChange={onFieldChange}
                userId={userId}
                onProceed={onNext || onDone}
                editDisabled={!hasAccess(ACCESS_RIGHTS.USER_LIMITS_EDIT)}>
                {({busy, loadingError, content, onSave, isChunkChanged}) => (
                    <CreateUserStep
                        selectedStep={CREATE_USER_STEPS.LIMITS}
                        onChangeStep={step => {
                            if (isChunkChanged) {
                                onShowModal(() => onRedirectToStep(step));
                            } else {
                                onRedirectToStep(step);
                            }
                        }}
                        loadingError={loadingError}
                        canViewLimits={canViewLimits}
                        canSetupAdminRole={canSetupAdminRole}
                        buttonsBlock={[
                            <Button key="Close" variant="Type.TERTIARY" onClick={onClose}>Close</Button>,
                            <Button
                                key="Back"
                                variant="Type.SECONDARY"
                                onClick={() => {
                                    if (isChunkChanged) {
                                        onShowModal(onBack);
                                    } else {
                                        onBack();
                                    }
                                }}>
                                Back
                            </Button>,
                            <Button
                                key="Done"
                                variant="Type.PRIMARY"
                                busy={busy}
                                onClick={() => onSave()}>
                                {onNext ? 'Save & Continue' : 'Finish'}
                            </Button>,
                        ]}
                        content={content}
                    />
                )}
            </UserLimitsController>
        </ValidationContextProvider>
    );
};
